// src/components/Blocks/CAMEvents/View.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Loader, Message, Button, Accordion, Icon, Label } from 'semantic-ui-react';
import { format, parseISO } from 'date-fns';
import './TCEvents.css';
import downIcon from '@plone/volto/icons/collapse-down.svg';
import upIcon from '@plone/volto/icons/collapse-up.svg';

const CAMEventsView = ({ data }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mathJaxLoaded, setMathJaxLoaded] = useState(false);

  const { title = '' } = data || {};

  // Load MathJax script
  useEffect(() => {
    if (!window.MathJax) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
      script.async = true;
      script.onload = () => {
        window.MathJax = {
          tex: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$', '$$'], ['\\[', '\\]']],
          },
          startup: {
            ready: () => {
              window.MathJax.startup.defaultReady();
              setMathJaxLoaded(true);
            }
          }
        };
      };
      document.head.appendChild(script);
    } else {
      setMathJaxLoaded(true);
    }
  }, []);

  // Typeset math after content updates
  useEffect(() => {
    if (mathJaxLoaded && window.MathJax && events.length > 0) {
      setTimeout(() => {
        window.MathJax.typesetPromise?.().catch((err) => 
          console.log('MathJax typeset error:', err)
        );
      }, 100);
    }
  }, [events, activeIndex, mathJaxLoaded]);

  // Fetch events from API
  const fetchEvents = useCallback(async () => {
    try {
      //console.log('Fetching events from API...');
      
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://events.tifrbng.res.in/api/latest?format=json', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const eventsData = await response.json();
      //console.log('Received events:', eventsData.length);
      
      if (!Array.isArray(eventsData)) {
        throw new Error('API did not return an array of events');
      }
      
      setEvents(eventsData);
      setLoading(false);
      //console.log('Events loaded successfully');
      
    } catch (err) {
      //console.error('Error fetching events:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleAccordionClick = useCallback((e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  }, [activeIndex]);

  const formatDate = useCallback((dateString) => {
    try {
      return format(parseISO(dateString), 'EEE, MMMM dd, yyyy');
    } catch {
      return dateString;
    }
  }, []);

  const formatTime = useCallback((dateString) => {
    try {
      return format(parseISO(dateString), 'h:mm a');
    } catch {
      return '';
    }
  }, []);

  const getEventTypeColor = useCallback((type) => {
    const colors = {
      'Workshop': 'purple',
      'Conference': 'teal'
    };
    return colors[type] || 'grey';
  }, []);

  // Process description: handle markdown, HTML, and preserve formatting
  const processDescription = useCallback((description) => {
    if (!description) return '';
    
    let processed = description;
    
    // Convert markdown-style links [text](url) to HTML
    processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Convert plain URLs to clickable links (if not already in HTML anchor)
    processed = processed.replace(
      /(?<!href=["'])(?<!">)(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    
    // Convert double line breaks to paragraph breaks
    processed = processed.replace(/\n\n+/g, '</p><p>');
    
    // Convert single line breaks to <br>
    processed = processed.replace(/\n/g, '<br>');
    
    // Wrap in paragraph if not already HTML
    if (!processed.includes('<p>') && !processed.includes('<div>')) {
      processed = `<p>${processed}</p>`;
    }
    
    return processed;
  }, []);

  if (loading) {
    return (
      <Container className="tcevents-block">
        <Loader active inline="centered" size="large">
          Loading events...
        </Loader>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="tcevents-block">
        <Message negative>
          <Message.Header>Error loading events</Message.Header>
          <p>{error}</p>
          <Button 
            primary 
            onClick={() => fetchEvents()}
            icon="refresh"
            content="Retry"
          />
        </Message>
      </Container>
    );
  }

  if (events.length === 0) {
    return (
      <Container className="tcevents-block">
        {title && <h2 className="block-title">{title}</h2>}
        <Message info icon>
          <Icon name="calendar times outline" />
          <Message.Content>
            <Message.Header>No upcoming events</Message.Header>
            <p>There are no events scheduled at this time. Please check back later.</p>
          </Message.Content>
        </Message>
      </Container>
    );
  }

  return (
    <Container className="tcevents-block">
      {title && <h2 className="block-title">{title}</h2>}

      <div className="events-list accordion-mode">
        <Accordion styled fluid>
          {events.map((event, index) => (
            <div key={event.id} className="event-item">
              <Accordion.Title
                active={activeIndex === index}
                index={index}
                onClick={handleAccordionClick}
                className="event-title-section"
              >
                <div className="event-header">
                  <div className="event-title-content">
                    <div className="event-meta">
                      <Label
                        color={getEventTypeColor(event.etype)} 
                        size="small"
                        className="event-type-label"
                      >
                        {event.etype}
                      </Label>
                      <span className="date-text">
                        {formatDate(event.start)} • {formatTime(event.start)}
                        {event.end && ` - ${formatTime(event.end)}`}
                      </span>
                    </div>

                    <h3 className="event-title">{event.title}</h3>

                    {event.speaker && (
                      <div className="speaker-preview">
                        <strong>Speaker:</strong> {event.speaker.first_name}{' '}{event.speaker.last_name}, {event.speaker.affiliation}                      
                      </div>
                    )}
                    {event.location && (
                      <div className="speaker-preview">
                        <strong>Venue: </strong> {event.location}
                        </div>
                      )}
                  </div>
                  <Icon name={activeIndex === index ? upIcon : downIcon} />
                </div>
              </Accordion.Title>
              
              <Accordion.Content 
                active={activeIndex === index}
                className="event-content"
              >
                <div className="event-details">
                  {event.description && (
                    <div className="description-section">
                      <h4 className="section-header">Abstract</h4>
                      <div 
                        className="event-description"
                        dangerouslySetInnerHTML={{ 
                          __html: processDescription(event.description) 
                        }}
                      />
                    </div>
                  )}

                </div>
              </Accordion.Content>
            </div>
          ))}
        </Accordion>
      </div>
    </Container>
  );
};

export default CAMEventsView;
